import React, { useState, useMemo } from 'react';
import { Search, Book, ChevronRight, HelpCircle, ShoppingCart, AlertTriangle } from 'lucide-react';

interface HelpArticle {
  id: string;
  title: string;
  content: string;
  category: string;
  tags: string[];
}

const helpArticles: HelpArticle[] = [
  {
    id: 'rapid-mode',
    title: 'Hogyan működik a Rapid Mód?',
    content:
      'A Rapid Módban a beolvasott tételek azonnal, megerősítés nélkül mentésre kerülnek. Ez lehetővé teszi a folyamatos, gyors szkennelést, de figyelem: a műveletek nem visszavonhatók! A Rapid Módot a villám ikonra kattintva vagy a Ctrl+R billentyűkombinációval kapcsolhatod be/ki.',
    category: 'Szkennelés',
    tags: ['rapid', 'mód', 'gyors', 'mentés'],
  },
  {
    id: 'barcode-not-recognized',
    title: 'Miért nem ismeri fel a szkenner a kódot?',
    content:
      'Ha a szkenner nem ismeri fel a vonalkódot, először ellenőrizd, hogy a kód tiszta és jól látható-e. Ha a termék még nincs az adatbázisban, az "Ismeretlen Termék" kártya jelenik meg, ahol megadhatod a termék nevét és árát. Belső vonalkód generálásához használd a "Nincs vonalkód? Generálás (20...)" gombot.',
    category: 'Szkennelés',
    tags: ['szkenner', 'vonalkód', 'ismeretlen', 'hiba'],
  },
  {
    id: 'out-of-stock',
    title: 'Mit jelent a "NINCS KÉSZLETEN!" figyelmeztetés?',
    content:
      'Ez a figyelmeztetés akkor jelenik meg, amikor egy termékből már nincs készlet (mennyiség = 0), és mégis megpróbálod eladni, visszavinni vagy selejtezni. A termék továbbra is látható marad a listában 0 mennyiséggel, így később hozzáadhatsz új készletet.',
    category: 'Készletkezelés',
    tags: ['készlet', 'figyelmeztetés', 'nulla', 'mennyiség'],
  },
  {
    id: 'undo-delete',
    title: 'Hogyan vonhatom vissza egy törlés műveletet?',
    content:
      'Ha véletlenül töröltél egy tételt, a törlés után megjelenő zöld értesítésben találsz egy "Visszavonás" gombot. Erre kattintva a tétel visszaállításra kerül. Fontos: ez csak 5 másodpercig érhető el, ezért gyorsan kell cselekedned!',
    category: 'Műveletek',
    tags: ['törlés', 'visszavonás', 'undo', 'helyreállítás'],
  },
  {
    id: 'autocomplete',
    title: 'Hogyan működik a terméknév autocomplete?',
    content:
      'Amikor új terméket adsz hozzá és elkezdesz gépelni a "Termék Neve" mezőbe, az alkalmazás automatikusan javaslatokat mutat a meglévő termékek közül. Ha több mint 2 karaktert írsz be, a legfeljebb 5 legjobban illeszkedő terméknév jelenik meg. Kattints egy javaslatra, hogy automatikusan kitöltse a mezőt.',
    category: 'Szkennelés',
    tags: ['autocomplete', 'javaslat', 'terméknév', 'gyors'],
  },
  {
    id: 'keyboard-shortcuts',
    title: 'Milyen billentyűparancsok érhetők el?',
    content:
      'Az alkalmazás több billentyűparancsot is támogat: Ctrl+K (vagy Cmd+K Mac-en): Keresés fókuszálása a Dashboard-on. Ctrl+R (vagy Cmd+R Mac-en): Rapid Mód be/ki kapcsolása a Szkennelés oldalon. Escape: Bármilyen megnyitott modal bezárása. Ctrl+D: Debug panel megjelenítése/elrejtése (csak fejlesztési környezetben).',
    category: 'Általános',
    tags: ['billentyűparancs', 'shortcut', 'gyors', 'hivatkozás'],
  },
  {
    id: 'print-labels',
    title: 'Hogyan nyomtathatok címkéket?',
    content:
      'A Dashboard-on minden tétel mellett találsz egy nyomtató ikont. Erre kattintva a tétel hozzáadódik a nyomtatási sorhoz. A "Címkék" menüpontban megtekintheted a várólistát, és egyetlen gombbal kinyomtathatod az összes címkét A4-es papírra. A nyomtatási sor localStorage-ban tárolódik, így akár oldal frissítés után is megmarad.',
    category: 'Nyomtatás',
    tags: ['címke', 'nyomtatás', 'várólista', 'A4'],
  },
  {
    id: 'offline-mode',
    title: 'Mi történik offline módban?',
    content:
      'Ha az internetkapcsolat megszakad, az alkalmazás automatikusan offline módba vált. Ebben az esetben az összes művelet helyi tárolásra kerül, és amikor a kapcsolat helyreáll, automatikusan szinkronizálódik a felhővel. A szinkronizálás állapotát a felső sávban láthatod.',
    category: 'Általános',
    tags: ['offline', 'szinkronizálás', 'internet', 'kapcsolat'],
  },
  {
    id: 'procurement-module',
    title: 'Beszerzés és Készletfigyelés',
    content: `Hogyan működik a készletfigyelés?

1. Limit beállítása:
   • Termék felvételekor vagy szerkesztésekor add meg a "Jelzési limit (db)" értékét.
   • Ha a készlet ez alá csökken, a rendszer automatikusan jelezni fog.

2. Kifogyóban lista:
   • A Készlet menüben a "Kifogyóban" fülön látod azokat a termékeket, amik a limit alá estek, de még nincsenek a bevásárlólistán.
   • Ez a te "Teendő listád" - itt láthatod, mely termékeket kell beszerezni.

3. A Bevásárlólista használata:
   • Listára tétel: A "Kifogyóban" listán kattints a Kosár ikonra (🛒) a termék mellett. Ekkor átkerül a "Bevásárlólista" fülre.
   • Nyomtatás és Export: A "Bevásárlólista" fülön használd a fenti gombokat:
     - Nyomtatás: Kattints a "Lista nyomtatása" gombra a lista kinyomtatásához.
     - Excel Export: Kattints az "Excel Export" gombra a lista Excel fájlba mentéséhez.
   • Kijelölés: A bevásárlólistán jelölj ki konkrét tételeket a checkboxokkal, és csak azokat nyomtasd ki.

4. Lista tisztítása:
   • Automatikus: Ha bevételezel a termékből (nő a készlet a limit fölé), a rendszer automatikusan leveszi a listáról.
   • Kézi: Ha mégsem akarod megrendelni, kattints újra a Kosár ikonra a levételhez.

Munkafolyamat: Kifogyóban → Bevásárlólista → Beszerzés → Automatikus eltávolítás`,
    category: 'Beszerzés',
    tags: [
      'beszerzés',
      'készletfigyelés',
      'limit',
      'kifogyóban',
      'bevásárlólista',
      'nyomtatás',
      'export',
    ],
  },
];

export const HelpCenter: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedArticle, setSelectedArticle] = useState<string | null>(null);

  const filteredArticles = useMemo(() => {
    if (!searchTerm.trim()) {
      return helpArticles;
    }

    const lowerSearch = searchTerm.toLowerCase();
    return helpArticles.filter(
      (article) =>
        article.title.toLowerCase().includes(lowerSearch) ||
        article.content.toLowerCase().includes(lowerSearch) ||
        article.category.toLowerCase().includes(lowerSearch) ||
        article.tags.some((tag) => tag.toLowerCase().includes(lowerSearch))
    );
  }, [searchTerm]);

  const selectedArticleData = selectedArticle
    ? helpArticles.find((a) => a.id === selectedArticle)
    : null;

  if (selectedArticleData) {
    return (
      <div className="w-full p-6">
        <button
          onClick={() => setSelectedArticle(null)}
          className="mb-4 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 flex items-center gap-2 text-sm font-medium"
        >
          <ChevronRight className="rotate-180" size={16} />
          Vissza a listához
        </button>
        <div className="bg-card rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center gap-2 mb-4">
            {selectedArticleData.id === 'procurement-module' && (
              <ShoppingCart size={20} className="text-green-600 dark:text-green-400" />
            )}
            <span className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase">
              {selectedArticleData.category}
            </span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            {selectedArticleData.title}
          </h2>
          <div className="prose prose-sm dark:prose-invert max-w-none">
            {selectedArticleData.id === 'procurement-module' ? (
              <div className="text-gray-700 dark:text-gray-300 leading-relaxed space-y-4">
                <div>
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400" />
                    Hogyan működik a készletfigyelés?
                  </h3>
                  <ol className="list-decimal list-inside space-y-3 ml-2">
                    <li>
                      <strong>Limit beállítása:</strong>
                      <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                        <li>
                          Termék felvételekor vagy szerkesztésekor add meg a "Jelzési limit (db)"
                          értékét.
                        </li>
                        <li>Ha a készlet ez alá csökken, a rendszer automatikusan jelezni fog.</li>
                      </ul>
                    </li>
                    <li>
                      <strong>Kifogyóban lista:</strong>
                      <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                        <li>
                          A Készlet menüben a "Kifogyóban" fülön látod azokat a termékeket, amik a
                          limit alá estek, de még nincsenek a bevásárlólistán.
                        </li>
                        <li>
                          Ez a te "Teendő listád" - itt láthatod, mely termékeket kell beszerezni.
                        </li>
                      </ul>
                    </li>
                    <li>
                      <strong>A Bevásárlólista használata:</strong>
                      <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                        <li>
                          <strong>Listára tétel:</strong> A "Kifogyóban" listán kattints a Kosár
                          ikonra (🛒) a termék mellett. Ekkor átkerül a "Bevásárlólista" fülre.
                        </li>
                        <li>
                          <strong>Nyomtatás és Export:</strong> A "Bevásárlólista" fülön használd a
                          fenti gombokat:
                          <ul className="list-circle list-inside ml-8 mt-1 space-y-1">
                            <li>
                              <strong>Nyomtatás:</strong> Kattints a "Lista nyomtatása" gombra a
                              lista kinyomtatásához.
                            </li>
                            <li>
                              <strong>Excel Export:</strong> Kattints az "Excel Export" gombra a
                              lista Excel fájlba mentéséhez.
                            </li>
                          </ul>
                        </li>
                        <li>
                          <strong>Kijelölés:</strong> A bevásárlólistán jelölj ki konkrét tételeket
                          a checkboxokkal, és csak azokat nyomtasd ki.
                        </li>
                      </ul>
                    </li>
                    <li>
                      <strong>Lista tisztítása:</strong>
                      <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                        <li>
                          <strong>Automatikus:</strong> Ha bevételezel a termékből (nő a készlet a
                          limit fölé), a rendszer automatikusan leveszi a listáról.
                        </li>
                        <li>
                          <strong>Kézi:</strong> Ha mégsem akarod megrendelni, kattints újra a Kosár
                          ikonra a levételhez.
                        </li>
                      </ul>
                    </li>
                  </ol>
                </div>
                <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-200">
                    <strong>Munkafolyamat:</strong> Kifogyóban → Bevásárlólista → Beszerzés →
                    Automatikus eltávolítás
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                {selectedArticleData.content}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Book className="text-blue-600 dark:text-blue-400" size={28} />
          Segítségközpont
        </h2>
        <div className="relative">
          <Search className="absolute left-3 top-2.5 text-gray-400 dark:text-gray-500" size={20} />
          <input
            type="text"
            placeholder="Keresés a dokumentációban..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
          />
        </div>
      </div>

      {filteredArticles.length === 0 ? (
        <div className="text-center p-10 bg-card rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <HelpCircle className="mx-auto mb-4 text-gray-400 dark:text-gray-500" size={48} />
          <p className="text-gray-600 dark:text-gray-400">Nincs találat a keresésre.</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
            Próbáld meg más kulcsszavakkal keresni.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredArticles.map((article) => {
            // Show icon for procurement module article
            const showIcon = article.id === 'procurement-module';
            return (
              <button
                key={article.id}
                onClick={() => setSelectedArticle(article.id)}
                className="text-left p-4 bg-card rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-400 hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {showIcon && (
                      <ShoppingCart size={16} className="text-green-600 dark:text-green-400" />
                    )}
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                      {article.category}
                    </span>
                  </div>
                  <ChevronRight size={16} className="text-gray-400 dark:text-gray-500" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                  {article.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                  {article.content.substring(0, 100)}...
                </p>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
